import { useLayoutEffect, useRef, useState } from 'react'

import { Box, Flex, Link, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

import { Article } from '@core/app/types/globalApiType'

import { formatArticleDate, toPlainText } from '../../utils'

interface Props {
    article: Article
}

const HEADING_LINE_HEIGHT = 24
const mobileCard = '@media (max-width: 639px)'
const phoneCard = '@media (max-width: 480px)'

const NewsCard = ({ article }: Props): JSX.Element => {
    const { t } = useTranslation()
    const ingress = toPlainText(article.Ingress || '')
    const headingRef = useRef<HTMLParagraphElement>(null)
    const [descriptionLines, setDescriptionLines] = useState(4)

    useLayoutEffect(() => {
        const heading = headingRef.current
        if (!heading) return

        const measure = () => {
            const lines = Math.round(heading.getBoundingClientRect().height / HEADING_LINE_HEIGHT)
            setDescriptionLines(lines > 1 ? 3 : 4)
        }

        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(heading)

        return () => observer.disconnect()
    }, [article.Heading])

    return (
        <Link
            href={`/newsfeed/${article.NewsfeedID}`}
            display={'block'}
            w={'full'}
            _hover={{ textDecoration: 'none' }}
        >
            <Flex
                role={'group'}
                h={'160px'}
                minH={'160px'}
                maxH={'160px'}
                boxSizing={'content-box'}
                border={'1px solid'}
                borderColor={'#EAEAEB'}
                borderRadius={'8px'}
                overflow={'hidden'}
                bg={'white'}
                transition={'all 0.15s ease'}
                _hover={{ borderColor: '#97979B' }}
                sx={{
                    [mobileCard]: {
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                        height: 'auto',
                        minHeight: 0,
                        maxHeight: 'none',
                    },
                    [phoneCard]: {
                        height: 'auto',
                        minHeight: 0,
                        maxHeight: 'none',
                    },
                }}
            >
                <Flex
                    direction={'column'}
                    flex={1}
                    minW={0}
                    overflow={'hidden'}
                    justify={'flex-start'}
                    px={'16px'}
                    py={'16px'}
                    _groupHover={{ bg: '#fafafa' }}
                    sx={{
                        [mobileCard]: {
                            order: 1,
                            flex: '0 0 auto',
                            height: 'auto',
                            minHeight: 'auto',
                        },
                    }}
                >
                    <Text fontSize={'12px'} fontWeight={'500'} color={'#717175'} lineHeight={'16px'}>
                        {formatArticleDate(article.Created, t)}
                    </Text>
                    <Text
                        ref={headingRef}
                        fontSize={'16px'}
                        fontWeight={'600'}
                        color={'#0C0C0D'}
                        lineHeight={'24px'}
                        noOfLines={2}
                        wordBreak={'break-word'}
                        mt={'6px'}
                    >
                        {article.Heading}
                    </Text>
                    {ingress ? (
                        <Text
                            fontSize={'12px'}
                            fontWeight={'500'}
                            color={'#4B4B4E'}
                            lineHeight={'18px'}
                            w={'full'}
                            minW={0}
                            mt={'4px'}
                            wordBreak={'break-word'}
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: descriptionLines,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {ingress}
                        </Text>
                    ) : null}
                </Flex>
                <Box
                    position={'relative'}
                    w={'320px'}
                    h={'160px'}
                    flexShrink={0}
                    overflow={'hidden'}
                    bg={'#F3F3F3'}
                    sx={{
                        [mobileCard]: {
                            order: 0,
                            width: '100%',
                            height: 'calc(392px * 2 / 3)',
                            flex: '0 0 auto',
                            minHeight: 0,
                        },
                        [phoneCard]: {
                            height: '165px',
                            flex: '0 0 auto',
                        },
                    }}
                >
                    <Box
                        position={'absolute'}
                        inset={0}
                        bgImage={article.Img ? `url(${article.Img})` : undefined}
                        bgSize={'cover'}
                        bgPosition={'center'}
                        bgRepeat={'no-repeat'}
                        transition={'transform 0.35s ease'}
                        _groupHover={article.Img ? { transform: 'scale(1.06)' } : undefined}
                    />
                </Box>
            </Flex>
        </Link>
    )
}

export default NewsCard
